
import React, { useState, useMemo, useEffect } from 'react';
import { Transaction, CreditCard, CategoryType } from '../types';
import { formatCurrency, formatDate } from '../utils/helpers';
import { CATEGORY_CONFIG } from '../constants';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

interface ReportsProps {
  transactions: Transaction[];
  cards: CreditCard[];
  onTriggerVerify: (title: string, message: string, onVerified: () => void, icon?: string) => void;
}

const Reports: React.FC<ReportsProps> = ({ transactions, cards, onTriggerVerify }) => {
  const [reportType, setReportType] = useState<'month' | 'year'>('month');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [detailModal, setDetailModal] = useState<{ isOpen: boolean; category: string | null }>({
    isOpen: false,
    category: null
  });

  const [showValues, setShowValues] = useState(() => {
    const saved = localStorage.getItem('equilibra_privacy_mode');
    return saved === null ? true : saved === 'true';
  });

  useEffect(() => {
    const handleSync = () => {
      const saved = localStorage.getItem('equilibra_privacy_mode');
      setShowValues(saved === null ? true : saved === 'true');
    };
    window.addEventListener('privacyModeChanged', handleSync);
    return () => window.removeEventListener('privacyModeChanged', handleSync);
  }, []);

  const togglePrivacy = () => {
    const newVal = !showValues;
    setShowValues(newVal);
    localStorage.setItem('equilibra_privacy_mode', newVal.toString());
    window.dispatchEvent(new Event('privacyModeChanged'));
  };

  const maskValue = (val: number) => showValues ? formatCurrency(val) : "R$ ••••";

  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const yearsSet = new Set<number>([currentYear]);
    transactions.forEach(t => {
      if (t.date) {
        const y = parseInt(t.date.split('-')[0]);
        if (y) yearsSet.add(y);
      }
    });
    return Array.from(yearsSet).sort((a, b) => a - b);
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      const d = new Date(t.date + 'T12:00:00');
      if (reportType === 'month') {
        return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
      }
      return d.getFullYear() === selectedYear;
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [transactions, reportType, selectedMonth, selectedYear]);

  const totals = useMemo(() => {
    return filteredTransactions.reduce((acc, t) => {
      if (t.type === 'income') {
        acc.income += t.amount;
      } else if (t.category !== 'Pagamento de Fatura') {
        acc.expense += t.amount;
      }
      return acc;
    }, { income: 0, expense: 0 });
  }, [filteredTransactions]);

  const categorySummary = useMemo(() => {
    const summary = filteredTransactions.reduce((acc, t) => {
      if (t.type === 'expense' && t.category !== 'Pagamento de Fatura') {
        acc[t.category] = (acc[t.category] || 0) + t.amount;
      }
      return acc;
    }, {} as Record<string, number>);
    return Object.entries(summary).sort((a, b) => (b[1] as number) - (a[1] as number));
  }, [filteredTransactions]);

  const topExpenses = useMemo(() => {
    const grouped = filteredTransactions
      .filter(t => t.type === 'expense' && t.category !== 'Pagamento de Fatura')
      .reduce((acc, t) => {
        const key = `${t.category}-${t.description.toLowerCase().trim()}`;
        if (!acc[key]) {
          acc[key] = { 
            id: key,
            category: t.category,
            description: t.description,
            amount: 0,
            count: 0,
            lastDate: t.date
          };
        }
        acc[key].amount += t.amount;
        acc[key].count += 1;
        if (new Date(t.date) > new Date(acc[key].lastDate)) {
          acc[key].lastDate = t.date;
        }
        return acc;
      }, {} as Record<string, { id: string; category: string; description: string; amount: number; count: number; lastDate: string }>);

    return Object.values(grouped)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }, [filteredTransactions]);

  const categoryDetails = useMemo(() => {
    if (!detailModal.category) return [];
    return filteredTransactions.filter(t => t.category === detailModal.category && t.type === 'expense');
  }, [filteredTransactions, detailModal.category]);

  const generatePDF = async () => {
    setIsGenerating(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const periodLabel = reportType === 'month' ? `${months[selectedMonth]} de ${selectedYear}` : `Ano de ${selectedYear}`;
      const generationDate = new Date().toLocaleDateString('pt-BR');

      doc.setFillColor(15, 23, 42); 
      doc.rect(0, 0, pageWidth, 45, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('EQUILIBRA FINANCE', 20, 25);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`RELATÓRIO FINANCEIRO DETALHADO | ${periodLabel.toUpperCase()}`, 20, 33);
      doc.text(`Emitido em: ${generationDate}`, pageWidth - 20, 25, { align: 'right' });

      let currentY = 55;
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Resumo de Caixa (Fluxo Real)', 20, currentY);
      currentY += 8;

      const cardWidth = (pageWidth - 48) / 3;
      doc.setFillColor(209, 250, 229);
      doc.roundedRect(20, currentY, cardWidth, 22, 3, 3, 'F');
      doc.setTextColor(5, 150, 105);
      doc.setFontSize(7);
      doc.text('TOTAL DE RECEITAS', 20 + cardWidth/2, currentY + 7, { align: 'center' });
      doc.setFontSize(11);
      doc.text(formatCurrency(totals.income), 20 + cardWidth/2, currentY + 16, { align: 'center' });

      doc.setFillColor(254, 242, 242);
      doc.roundedRect(24 + cardWidth, currentY, cardWidth, 22, 3, 3, 'F');
      doc.setTextColor(220, 38, 38);
      doc.setFontSize(7);
      doc.text('SAÍDAS (CONSUMO REAL)', 24 + cardWidth + cardWidth/2, currentY + 7, { align: 'center' });
      doc.setFontSize(11);
      doc.text(formatCurrency(totals.expense), 24 + cardWidth + cardWidth/2, currentY + 16, { align: 'center' });

      doc.setFillColor(248, 250, 252);
      doc.roundedRect(28 + cardWidth * 2, currentY, cardWidth, 22, 3, 3, 'F');
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(7);
      doc.text('BALANÇO FINAL', 28 + cardWidth * 2 + cardWidth/2, currentY + 7, { align: 'center' });
      doc.setFontSize(11);
      doc.text(formatCurrency(totals.income - totals.expense), 28 + cardWidth * 2 + cardWidth/2, currentY + 16, { align: 'center' });

      currentY += 35;

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Composição de Gastos por Categoria', 20, currentY);
      
      autoTable(doc, {
        startY: currentY + 5,
        head: [['Categoria', 'Montante', '% do Total']],
        body: categorySummary.map(([cat, amount]) => [
          cat, 
          formatCurrency(amount), 
          ((amount / (totals.expense || 1)) * 100).toFixed(1) + '%'
        ]),
        theme: 'striped',
        headStyles: { fillColor: [71, 85, 105] },
        styles: { fontSize: 8 },
        margin: { left: 20, right: 20 }
      });

      currentY = (doc as any).lastAutoTable.finalY + 15;
      doc.setTextColor(16, 185, 129);
      doc.setFontSize(14);
      doc.text('Detalhamento de Receitas (Entradas)', 20, currentY);

      const incomes = filteredTransactions.filter(t => t.type === 'income');
      autoTable(doc, {
        startY: currentY + 5,
        head: [['Data', 'Categoria', 'Descrição', 'Valor']],
        body: incomes.map(t => [formatDate(t.date), t.category, t.description, formatCurrency(t.amount)]),
        theme: 'grid',
        headStyles: { fillColor: [16, 185, 129] },
        styles: { fontSize: 8 },
        margin: { left: 20, right: 20 }
      });

      currentY = (doc as any).lastAutoTable.finalY + 15;
      if (currentY > 240) { doc.addPage(); currentY = 20; }
      doc.setTextColor(239, 68, 68);
      doc.setFontSize(14);
      doc.text('Detalhamento de Saídas (Consumo Real)', 20, currentY);

      const expenses = filteredTransactions.filter(t => t.type === 'expense' && t.category !== 'Pagamento de Fatura');
      autoTable(doc, {
        startY: currentY + 5,
        head: [['Data', 'Categoria', 'Descrição', 'Valor']],
        body: expenses.map(t => [formatDate(t.date), t.category, t.description, formatCurrency(t.amount)]),
        theme: 'grid',
        headStyles: { fillColor: [239, 68, 68] },
        styles: { fontSize: 8 },
        margin: { left: 20, right: 20 }
      });

      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text(`Página ${i} de ${totalPages} | Gerado por Equilibra Finance App`, pageWidth / 2, 285, { align: 'center' });
      }

      doc.save(`Relatorio_Equilibra_${periodLabel.replace(/\s/g, '_')}.pdf`);
    } catch (err) {
      alert('Erro ao gerar PDF profissional');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="animate-in fade-in duration-700 pb-24 max-w-6xl mx-auto px-1">
      <div className="sticky top-0 z-40 bg-slate-100/80 dark:bg-slate-950/80 backdrop-blur-xl -mx-4 px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-4 border-b border-slate-200/50 dark:border-slate-800/50 md:relative md:bg-transparent md:border-none md:p-0 md:mb-6 transition-all">
        <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-4">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-black text-slate-900 dark:text-slate-50 heading-display uppercase tracking-tight">Análises</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
              <p className="text-slate-500 dark:text-slate-400 text-[9px] md:text-xs uppercase font-black tracking-[0.15em]">Comportamento Financeiro • Soberania de Dados</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <button 
              onClick={togglePrivacy}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-500 w-9 h-9 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center shadow-premium active:scale-95 transition-all hover:border-blue-500/50 group"
              title={showValues ? "Ocultar Valores" : "Mostrar Valores"}
            >
              <i className={`fa-solid ${showValues ? 'fa-eye' : 'fa-eye-slash'} text-xs md:text-base lg:text-lg transition-transform group-hover:scale-110`}></i>
            </button>
            <button 
              onClick={() => {
                onTriggerVerify(
                  'Gerar Relatório PDF',
                  'Para exportar seus dados financeiros em um documento profissional, confirme sua Chave Mestra.',
                  generatePDF,
                  'fa-file-pdf'
                );
              }}
              disabled={filteredTransactions.length === 0 || isGenerating}
              className="bg-slate-900 dark:bg-blue-600 text-white px-4 md:px-6 h-9 md:h-10 lg:h-12 rounded-xl font-black text-[9px] uppercase tracking-[0.15em] shadow-xl flex items-center gap-2 md:gap-3 active:scale-95 transition-all flex-1 md:flex-none justify-center group"
            >
              {isGenerating ? <i className="fa-solid fa-circle-notch animate-spin"></i> : <i className="fa-solid fa-file-pdf text-sm md:text-base transition-transform group-hover:scale-110"></i>}
              PDF PROFISSIONAL
            </button>
          </div>
        </header>

        <div className="bg-white dark:bg-slate-900 p-1.5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-premium flex flex-col md:flex-row gap-2 md:gap-3 items-center mt-4">
          <div className="w-full md:w-48 lg:w-56">
            <div className="flex bg-slate-50 dark:bg-slate-800/50 p-1 rounded-lg border border-slate-100 dark:border-slate-800">
              <button onClick={() => setReportType('month')} className={`flex-1 py-1.5 md:py-2 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${reportType === 'month' ? 'bg-white dark:bg-slate-700 text-blue-600 shadow-sm' : 'text-slate-400'}`}>Mês</button>
              <button onClick={() => setReportType('year')} className={`flex-1 py-1.5 md:py-2 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${reportType === 'year' ? 'bg-white dark:bg-slate-700 text-blue-600 shadow-sm' : 'text-slate-400'}`}>Ano</button>
            </div>
          </div>
          <div className="flex gap-2 w-full md:flex-1">
            {reportType === 'month' && (
              <div className="flex-1 relative">
                <select value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))} className="w-full bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2.5 md:p-3 text-[9px] font-black text-slate-800 dark:text-slate-100 outline-none cursor-pointer appearance-none border border-slate-100 dark:border-slate-800 hover:border-blue-500/30 transition-colors uppercase tracking-widest">
                  {months.map((m, i) => <option key={m} value={i}>{m}</option>)}
                </select>
                <i className="fa-solid fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-[9px]"></i>
              </div>
            )}
            <div className="w-24 md:w-28 lg:w-32 relative">
              <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))} className="w-full bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2.5 md:p-3 text-[9px] font-black text-slate-800 dark:text-slate-100 outline-none cursor-pointer appearance-none border border-slate-100 dark:border-slate-800 hover:border-blue-500/30 transition-colors uppercase tracking-widest">
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <i className="fa-solid fa-calendar absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-[9px]"></i>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-6 mt-4 md:mt-0">
        <div className="bg-emerald-50 dark:bg-emerald-950/20 p-4 md:p-6 rounded-3xl border border-emerald-100 dark:border-emerald-900/30 shadow-xl shadow-emerald-500/5 group hover:-translate-y-1 transition-all duration-500">
          <p className="text-emerald-600 dark:text-emerald-400 text-[8px] md:text-[9px] font-black uppercase tracking-[0.15em] mb-1.5 md:mb-2">Receitas Totais</p>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-black text-emerald-700 dark:text-emerald-300 tracking-tighter leading-none heading-display">{maskValue(totals.income)}</h2>
        </div>
        <div className="bg-red-50 dark:bg-red-950/20 p-4 md:p-6 rounded-3xl border border-red-100 dark:border-red-900/30 shadow-xl shadow-red-500/5 group hover:-translate-y-1 transition-all duration-500">
          <p className="text-red-600 dark:text-red-400 text-[8px] md:text-[9px] font-black uppercase tracking-[0.15em] mb-1.5 md:mb-2">Saídas (Consumo)</p>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-black text-red-700 dark:text-red-300 tracking-tighter leading-none heading-display">{maskValue(totals.expense)}</h2>
        </div>
        <div className="bg-white dark:bg-slate-900 p-4 md:p-6 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-premium group hover:-translate-y-1 transition-all duration-500">
          <p className="text-slate-400 text-[8px] md:text-[9px] font-black uppercase tracking-[0.15em] mb-1.5 md:mb-2">Balanço Real</p>
          <h2 className={`text-xl md:text-2xl lg:text-3xl font-black tracking-tighter leading-none heading-display ${totals.income - totals.expense >= 0 ? 'text-slate-900 dark:text-slate-100' : 'text-red-600'}`}>{maskValue(totals.income - totals.expense)}</h2>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 items-start mb-6">
        <div className="bg-white dark:bg-slate-900 p-4 md:p-6 lg:p-8 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-premium">
          <h3 className="font-black text-slate-900 dark:text-slate-100 text-[9px] md:text-[10px] uppercase tracking-[0.15em] mb-6 md:mb-8 flex items-center gap-2 md:gap-3">
             <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-500 flex items-center justify-center">
               <i className="fa-solid fa-chart-pie text-sm md:text-base"></i>
             </div>
             Consumo por Categoria
          </h3>
          <div className="space-y-4 md:space-y-6">
            {categorySummary.length > 0 ? categorySummary.map(([cat, amount]) => (
              <button 
                key={cat} 
                onClick={() => setDetailModal({ isOpen: true, category: cat })}
                className="w-full text-left group transition-all"
              >
                <div className="flex justify-between text-[9px] md:text-[10px] font-black mb-1.5 md:mb-2">
                  <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1.5 md:gap-2 uppercase tracking-widest group-hover:text-blue-500 transition-colors">
                    <div className={`w-1.5 h-1.5 rounded-full ${CATEGORY_CONFIG[cat as CategoryType]?.color || 'bg-slate-400'} shadow-lg shadow-current/50`}></div>
                    {cat}
                  </span>
                  <span className="text-slate-900 dark:text-slate-100 heading-display text-[10px] md:text-xs">{maskValue(amount)}</span>
                </div>
                <div className="w-full bg-slate-100 dark:bg-slate-800/50 h-2.5 md:h-3 rounded-full overflow-hidden border border-slate-100 dark:border-slate-800 p-0.5">
                  <div 
                    className={`${CATEGORY_CONFIG[cat as CategoryType]?.color || 'bg-slate-400'} h-full transition-all duration-1000 shadow-lg shadow-current/20 rounded-full`} 
                    style={{ width: `${(amount / (totals.expense || 1)) * 100}%` }}
                  ></div>
                </div>
              </button>
            )) : (
              <div className="py-12 md:py-16 text-center opacity-30 flex flex-col items-center">
                 <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-50 dark:bg-slate-800/50 rounded-2xl flex items-center justify-center mb-3 md:mb-4">
                   <i className="fa-solid fa-receipt text-xl md:text-2xl"></i>
                 </div>
                 <p className="text-[8px] md:text-[9px] font-black uppercase tracking-[0.2em]">Sem dados no período</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 p-4 md:p-6 lg:p-8 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-premium">
          <h3 className="font-black text-slate-900 dark:text-slate-100 text-[9px] md:text-[10px] uppercase tracking-[0.15em] mb-6 md:mb-8 flex items-center gap-2 md:gap-3">
             <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-500 flex items-center justify-center">
               <i className="fa-solid fa-crown text-sm md:text-base"></i>
             </div>
             Maiores Consumos Reais
          </h3>
          <div className="space-y-3 md:space-y-4">
            {topExpenses.length > 0 ? topExpenses.map((t, idx) => (
              <div key={t.id} className="flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 hover:scale-[1.01] hover:bg-white dark:hover:bg-slate-800 transition-all group duration-500">
                <div className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-xl bg-white dark:bg-slate-900 flex items-center justify-center font-black text-[9px] md:text-[10px] text-slate-400 border border-slate-200 dark:border-slate-700 shadow-sm shrink-0 group-hover:text-amber-500 group-hover:border-amber-500/30 transition-all duration-500">
                  #{idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 md:gap-2 mb-0.5 md:mb-1">
                    <h4 className="text-[11px] md:text-[12px] lg:text-[13px] font-black text-slate-800 dark:text-slate-100 truncate leading-none heading-display uppercase tracking-tight">{t.description}</h4>
                    {t.count > 1 && (
                      <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-[7px] md:text-[8px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-widest">
                        {t.count}x
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 md:gap-2">
                    <span className="text-[7px] md:text-[8px] font-black text-slate-400 uppercase tracking-widest">{t.category}</span>
                    <div className="w-0.5 h-0.5 rounded-full bg-slate-300 dark:bg-slate-700"></div>
                    <span className="text-[7px] md:text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                      {t.count > 1 ? 'Último em ' : ''}{formatDate(t.lastDate)}
                    </span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[12px] md:text-[13px] lg:text-[14px] font-black text-red-600 dark:text-red-400 heading-display">{maskValue(t.amount)}</p>
                </div>
              </div>
            )) : (
              <div className="py-12 md:py-16 text-center opacity-30 flex flex-col items-center">
                 <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-50 dark:bg-slate-800/50 rounded-2xl flex items-center justify-center mb-3 md:mb-4">
                   <i className="fa-solid fa-ranking-star text-xl md:text-2xl"></i>
                 </div>
                 <p className="text-[8px] md:text-[9px] font-black uppercase tracking-[0.2em]">Nenhum gasto registrado</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {detailModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden border border-white/10 animate-in zoom-in-95 duration-200 max-h-[90vh]">
            <div className="bg-[#ef4444] px-6 md:px-10 py-6 flex justify-between items-center shrink-0">
              <div>
                <h3 className="text-white font-black text-lg md:text-2xl uppercase tracking-tighter leading-none">Detalhamento de Consumo</h3>
                <p className="text-white/80 text-[10px] font-black uppercase tracking-widest mt-2 flex items-center gap-2">
                  <span className="opacity-60">CATEGORIA:</span> {detailModal.category}
                </p>
              </div>
              <button 
                onClick={() => setDetailModal({ isOpen: false, category: null })}
                className="w-11 h-11 rounded-full bg-white/20 text-white hover:bg-white/30 transition-all flex items-center justify-center shadow-lg"
              >
                <i className="fa-solid fa-times text-lg"></i>
              </button>
            </div>
            
            <div className="overflow-y-auto flex-1 hide-scrollbar bg-white dark:bg-slate-900">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-50 dark:bg-slate-800/90 backdrop-blur-md border-b border-slate-100 dark:border-slate-800/50">
                    <th className="px-6 md:px-10 py-5 text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">Data</th>
                    <th className="px-4 py-5 text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">Descrição</th>
                    <th className="px-6 md:px-10 py-5 text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] text-right">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-800/30">
                  {categoryDetails.map((t) => {
                    const card = t.cardId ? cards.find(c => c.id === t.cardId) : null;
                    return (
                      <tr key={t.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors group">
                        <td className="px-6 md:px-10 py-5 text-[10px] md:text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-tighter">
                          {formatDate(t.date)}
                        </td>
                        <td className="px-4 py-5">
                          <div className="flex flex-col gap-1">
                            <span className="text-[12px] md:text-[13px] font-black text-slate-800 dark:text-slate-100 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                              {t.description}
                            </span>
                            {card && (
                              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 w-fit">
                                <i className="fa-solid fa-credit-card text-blue-500 text-[8px]"></i>
                                <span className="text-[8px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest">
                                  {card.name}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 md:px-10 py-5 text-[12px] md:text-[14px] font-mono font-black text-red-600 dark:text-red-400 text-right">
                          {maskValue(t.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {categoryDetails.length === 0 && (
                <div className="py-20 text-center text-slate-200 dark:text-slate-800">
                  <i className="fa-solid fa-receipt text-6xl mb-4"></i>
                  <p className="text-[10px] font-black uppercase tracking-widest">Nenhum detalhe disponível</p>
                </div>
              )}
            </div>
            
            <div className="bg-slate-50 dark:bg-slate-800/60 px-6 md:px-10 py-7 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center shrink-0 shadow-[0_-10px_30px_rgba(0,0,0,0.02)]">
               <div>
                 <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] block mb-1">Total de Consumo</span>
                 <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-tight">
                    {months[selectedMonth]} de {selectedYear}
                 </p>
               </div>
               <div className="text-right">
                 <span className="text-2xl md:text-3xl font-black text-red-600 dark:text-red-500 tracking-tighter leading-none">
                   {maskValue(categoryDetails.reduce((acc, t) => acc + t.amount, 0))}
                 </span>
               </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
